import { useState, type FormEvent } from 'react';
import { api, ApiRequestError } from '../api.ts';
import { useAuth } from '../auth/useAuth.ts';

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
    } catch (err) {
      // Deliberately show whatever generic message the backend sent back —
      // never guess at a more specific reason a login failed.
      setError(err instanceof ApiRequestError ? err.message : 'Something went wrong signing in.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="login-heading">
      {/* h2, not h3 (T-100): this is the first heading under the masthead's
       * h1 on the signed-out login/register route (see App.tsx's
       * `LoginGate`) — an h3 here skipped a level. */}
      <h2 id="login-heading">Sign in</h2>
      <p className="hint">Your squads and analysis are tied to your account.</p>

      <form onSubmit={onSubmit}>
        <label htmlFor="login-email">Email</label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label htmlFor="login-password" className="mt-lg">
          Password
        </label>
        <input
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && (
          <ul className="notices mt-xl">
            <li className="error">{error}</li>
          </ul>
        )}

        <div className="row">
          <button type="submit" disabled={submitting} aria-busy={submitting}>
            {submitting && <span className="spinner" aria-hidden="true" />}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <a className="ghost" href={api.auth.googleSignInUrl()}>
            Sign in with Google
          </a>
        </div>
      </form>

      <p className="hint mt-xl">No account yet?</p>
      <div className="row">
        <button type="button" className="ghost" onClick={onSwitchToRegister}>
          Register
        </button>
      </div>
    </section>
  );
}
